/**
 * Whitelabel Branding Service
 * 
 * Manages dynamic branding for multi-client whitelabel deployments.
 * Handles:
 * - Organization detection from URL/subdomain
 * - Branding asset loading and caching
 * - CSS variable application
 * - Feature visibility based on org configuration
 * 
 * Usage:
 *   import whitelabelBranding from '@/services/whitelabelBrandingService';
 * 
 *   // Load branding on app mount
 *   await whitelabelBranding.initialize();
 * 
 *   // Get branding values
 *   const logo = whitelabelBranding.getLogo();
 *   const primaryColor = whitelabelBranding.getColor('primary');
 * 
 *   // Check feature visibility
 *   if (whitelabelBranding.canShowFeature('carrier_analytics')) {
 *     // Show feature
 *   }
 */

import api from './api';
import {
  isLearningPortalOrg,
  isVetMediaProductHost,
  resolveOrgSlugFromHost,
} from '@/utils/associationPortal';

/** Shared SPA ships ADI assets at these paths — never leave them on Vet Media hosts. */
const ADI_DEFAULT_FAVICON_PATHS = new Set([
  '/favicon.ico',
  '/favicon.svg',
  '/favicon-96x96.png',
  '/apple-touch-icon.png',
]);

class WhitelabelBrandingService {
  constructor() {
    this.currentBranding = null;
    this.currentOrganization = null;
    this.isInitialized = false;
    
    // Default branding (ADI - Actionable Document Intelligence)
    this.defaultBranding = {
      logo_url: '/img/adi-logo.svg',
      logo_dark_url: '/img/adi-logo-dark.svg',
      favicon_url: '/favicon.ico',
      app_name: 'ADI - Actionable Document Intelligence',
      
      colors: {
        primary: '#1E40AF',
        secondary: '#10B981',
        accent: '#F59E0B',
        background: '#F9FAFB',
        surface: '#FFFFFF',
        text: '#111827',
        text_light: '#6B7280',
        error: '#DC2626',
        warning: '#F59E0B',
        success: '#10B981',
        info: '#3B82F6',
      },
      
      typography: {
        font_family: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        heading_font: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      },
      
      navigation: {
        show_powered_by: true,
        custom_footer_text: '',
        support_email: 'support@get-adi.com',
        support_phone: '',
      },
      
      features: {
        show_learning_module: true,
        show_carrier_analytics: true,
        show_rag_analysis: true,
        show_decision_center: true,
        show_data_suggestions: true,
      },
      
      oauth_providers: {
        google: { enabled: true },
        microsoft: { enabled: true },
        linkedin: { enabled: false },
        apple: { enabled: false },
      },
    };

    // Veterinary Media product defaults (shared SPA on veterinarymedia.ai /
    // vetmediagroup.com still ships ADI's /favicon.* in index.html).
    this.vetMediaProductBranding = {
      logo_url: '/img/adi-logo.svg',
      logo_dark_url: '/img/adi-logo-dark.svg',
      favicon_url: '/vetmedia-favicon.svg',
      favicons: {
        favicon: '/vetmedia-favicon.ico',
        favicon_svg: '/vetmedia-favicon.svg',
        favicon_96: '/vetmedia-favicon-96x96.png',
        apple_touch_icon: '/vetmedia-apple-touch-icon.png',
      },
      app_name: 'Veterinary Media.ai',
      colors: {
        primary: '#1B4F8C',
        secondary: '#D4A017',
        accent: '#C8102E',
        background: '#F9FAFB',
        surface: '#FFFFFF',
        text: '#0F1F3D',
        text_light: '#6B7280',
        error: '#DC2626',
        warning: '#F59E0B',
        success: '#10B981',
        info: '#3B82F6',
      },
      typography: {
        font_family: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        heading_font: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      },
      navigation: {
        show_powered_by: true,
        custom_footer_text: '© Veterinary Media.ai · A Colibri Media Company',
        support_email: 'support@veterinarymedia.ai',
        support_phone: '',
      },
      features: {
        show_learning_module: true,
        show_carrier_analytics: false,
        show_rag_analysis: true,
        show_decision_center: false,
        show_data_suggestions: false,
      },
      oauth_providers: {
        google: { enabled: true },
        microsoft: { enabled: false },
        linkedin: { enabled: false },
        apple: { enabled: true },
      },
    };
  }

  /**
   * Product-level default branding for the current host (ADI vs Vet Media).
   * @returns {Object}
   */
  getProductDefaultBranding() {
    if (isVetMediaProductHost()) {
      return this.vetMediaProductBranding;
    }
    return this.defaultBranding;
  }

  /**
   * Replace ADI shell favicons when org branding still points at defaults.
   * @param {Object} branding
   * @param {string|null} orgSlug
   * @returns {Object}
   */
  ensureProductFavicons(branding, orgSlug = null) {
    if (!branding) return branding;
    const onVetMediaSurface = isVetMediaProductHost() || isLearningPortalOrg(orgSlug);
    if (!onVetMediaSurface) return branding;

    const faviconUrl = branding.favicon_url || '';
    const logoFavicon = branding.logos?.favicon || '';
    const usesAdiDefault = ADI_DEFAULT_FAVICON_PATHS.has(faviconUrl)
      || faviconUrl.endsWith('/favicon.ico')
      || faviconUrl.endsWith('/favicon.svg');
    const hasCustomLogoFavicon = Boolean(logoFavicon)
      && !ADI_DEFAULT_FAVICON_PATHS.has(logoFavicon)
      && !logoFavicon.endsWith('/favicon.ico');

    if (hasCustomLogoFavicon) {
      return {
        ...branding,
        favicon_url: logoFavicon,
      };
    }

    if (usesAdiDefault || !faviconUrl) {
      return {
        ...branding,
        favicon_url: this.vetMediaProductBranding.favicon_url,
        favicons: {
          ...(branding.favicons || {}),
          ...this.vetMediaProductBranding.favicons,
        },
        app_name: branding.app_name
          && branding.app_name !== this.defaultBranding.app_name
          ? branding.app_name
          : this.vetMediaProductBranding.app_name,
      };
    }

    return branding;
  }

  /**
   * Initialize branding service
   * Detects organization and loads branding
   * 
   * @returns {Promise<void>}
   */
  async initialize() {
    if (this.isInitialized) {
      return;
    }

    try {
      // Detect organization from current context
      const orgSlug = this.detectOrganizationSlug();
      console.log('🔍 Detected organization slug:', orgSlug);
      
      if (orgSlug) {
        console.log('📡 Loading branding for organization:', orgSlug);
        await this.loadOrganizationBranding(orgSlug);
      } else {
        console.log('⚠️ No organization detected, using product default branding');
        this.currentBranding = this.getProductDefaultBranding();
        this.applyBranding();
      }
      
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize branding:', error);
      this.currentBranding = this.getProductDefaultBranding();
      this.applyBranding();
      this.isInitialized = true;
    }
  }

  /**
   * Detect organization slug from various sources
   * 
   * Priority:
   * 1. URL parameter (?org=slug)
   * 2. Subdomain (slug.app.praetorianai.io)
   * 3. LocalStorage (from previous login)
   * 4. User object (if authenticated)
   * 
   * @returns {string|null} Organization slug or null
   */
  detectOrganizationSlug() {
    // 1. Check URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const orgParam = urlParams.get('org');
    if (orgParam) {
      return orgParam;
    }

    // 2. Check for custom domain mappings
    const hostname = window.location.hostname.toLowerCase();
    
    const hostOrgSlug = resolveOrgSlugFromHost(hostname);
    if (hostOrgSlug) {
      console.log('🌐 Detected association portal host:', hostname, '→', hostOrgSlug);
      return hostOrgSlug;
    }

    // 3. Check subdomain
    const parts = hostname.split('.');
    
    // Assume format: {slug}.app.praetorianai.io or {slug}.localhost
    if (parts.length >= 3 || (parts.length === 2 && hostname.includes('localhost'))) {
      const subdomain = parts[0];
      
      // Skip common subdomains
      if (!['www', 'app', 'api', 'admin', 'dev'].includes(subdomain)) {
        return subdomain;
      }
    }

    // 4. Check localStorage from previous session
    try {
      const storedOrg = localStorage.getItem('organization');
      if (storedOrg) {
        const org = JSON.parse(storedOrg);
        return org.slug;
      }
    } catch (e) {
      // Ignore parse errors
    }

    // 5. Check authenticated user's organization
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.organization?.slug;
      }
    } catch (e) {
      // Ignore parse errors
    }

    return null;
  }

  /**
   * Load branding for specific organization
   * 
   * @param {string} orgSlug - Organization slug
   * @returns {Promise<Object>} Branding configuration
   */
  async loadOrganizationBranding(orgSlug) {
    try {
      console.log(`📡 Fetching branding from /users/organizations/${orgSlug}/`);
      
      // Fetch organization details with branding
      const response = await api.get(`/users/organizations/${orgSlug}/`);
      
      console.log('✅ Organization data received:', response.data);
      console.log('🎨 Branding settings:', response.data.branding_settings);
      
      this.currentOrganization = response.data;
      
      // Transform backend branding format to frontend format
      const brandingSettings = response.data.branding_settings || {};
      
      // Map backend format (logos.primary) to frontend format (logo_url)
      if (brandingSettings.logos) {
        brandingSettings.logo_url = brandingSettings.logos.primary;
        brandingSettings.logo_dark_url = brandingSettings.logos.secondary;
        brandingSettings.favicon_url = brandingSettings.logos.favicon;
      }
      
      // Merge organization branding with product defaults (ADI vs Vet Media)
      this.currentBranding = this.ensureProductFavicons(
        this.mergeBranding(
          this.getProductDefaultBranding(),
          brandingSettings
        ),
        orgSlug
      );
      
      console.log('🎨 Merged branding:', this.currentBranding);
      
      // Apply branding to DOM
      this.applyBranding();
      
      // Cache in localStorage
      this.cacheBranding(orgSlug, this.currentBranding);
      
      return this.currentBranding;
    } catch (error) {
      console.error(`❌ Failed to load branding for ${orgSlug}:`, error);
      
      // Try to use cached branding
      const cached = this.getCachedBranding(orgSlug);
      if (cached) {
        console.log('💾 Using cached branding');
        this.currentBranding = this.ensureProductFavicons(cached, orgSlug);
        this.applyBranding();
        return this.currentBranding;
      }
      
      // Fallback to product default
      console.log('⚠️ Using product default branding as fallback');
      this.currentBranding = this.getProductDefaultBranding();
      this.applyBranding();
      throw error;
    }
  }

  /**
   * Deep merge branding objects
   * 
   * @param {Object} defaults - Default branding
   * @param {Object} custom - Custom branding overrides
   * @returns {Object} Merged branding
   */
  mergeBranding(defaults, custom) {
    const merged = { ...defaults };
    
    for (const key in custom) {
      if (custom[key] && typeof custom[key] === 'object' && !Array.isArray(custom[key])) {
        // Recursively merge objects
        merged[key] = {
          ...(merged[key] || {}),
          ...custom[key],
        };
      } else {
        // Override primitive values
        merged[key] = custom[key];
      }
    }
    
    return merged;
  }

  /**
   * Apply branding to the DOM
   * Updates CSS variables, favicon, page title, etc.
   */
  applyBranding() {
    const branding = this.currentBranding || this.defaultBranding;
    
    // Apply CSS variables for colors
    const root = document.documentElement;
    if (branding.colors) {
      Object.entries(branding.colors).forEach(([key, value]) => {
        // Convert camelCase to kebab-case (e.g., textLight -> text-light)
        const kebabKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
        const cssVarName = `--color-${kebabKey}`;
        root.style.setProperty(cssVarName, value);
      });
    }
    
    // Apply custom CSS variables directly (overrides computed ones above)
    if (branding.cssVariables) {
      Object.entries(branding.cssVariables).forEach(([varName, value]) => {
        root.style.setProperty(varName, value);
      });
    }
    
    // Apply typography
    if (branding.typography) {
      if (branding.typography.font_family) {
        root.style.setProperty('--font-family', branding.typography.font_family);
      }
      if (branding.typography.heading_font) {
        root.style.setProperty('--font-heading', branding.typography.heading_font);
      }
    }
    
    // Update favicon (handle both single favicon_url and favicons object)
    if (branding.favicons && (branding.favicons.favicon || branding.favicons.favicon_svg)) {
      this.updateFaviconSet(branding.favicons);
    } else if (branding.favicon_url) {
      this.updateFavicon(branding.favicon_url);
    }
    
    // Update page title
    if (branding.app_name) {
      document.title = branding.app_name;
    }
  }

  /**
   * Update favicon
   * 
   * @param {string} faviconUrl - URL to favicon
   */
  updateFavicon(faviconUrl) {
    const links = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
    if (links.length === 0) {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = faviconUrl;
      document.head.appendChild(link);
      return;
    }
    links.forEach((link) => {
      link.href = faviconUrl;
    });
  }

  /**
   * Update complete favicon set (ICO, SVG, Apple Touch Icon, etc.)
   * 
   * @param {Object} favicons - Favicon URLs object
   */
  updateFaviconSet(favicons) {
    // SVG first (modern browsers prefer type=image/svg+xml)
    if (favicons.favicon_svg) {
      let svgLink = document.querySelector('link[rel="icon"][type="image/svg+xml"]');
      if (!svgLink) {
        svgLink = document.createElement('link');
        svgLink.rel = 'icon';
        svgLink.type = 'image/svg+xml';
        document.head.insertBefore(svgLink, document.head.firstChild);
      }
      svgLink.href = favicons.favicon_svg;
    }

    // Standard favicon (ICO / any)
    if (favicons.favicon) {
      let link = document.querySelector('link[rel="icon"][sizes="any"]')
        || document.querySelector('link[rel="icon"]:not([type="image/svg+xml"]):not([sizes="96x96"])');
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        link.sizes = 'any';
        document.head.appendChild(link);
      }
      link.href = favicons.favicon;
    }

    // Update Apple Touch Icon
    if (favicons.apple_touch_icon) {
      let appleLink = document.querySelector('link[rel="apple-touch-icon"]');
      if (!appleLink) {
        appleLink = document.createElement('link');
        appleLink.rel = 'apple-touch-icon';
        document.head.appendChild(appleLink);
      }
      appleLink.href = favicons.apple_touch_icon;
    }

    // Update 96x96 favicon
    if (favicons.favicon_96) {
      let favicon96 = document.querySelector('link[rel="icon"][sizes="96x96"]');
      if (!favicon96) {
        favicon96 = document.createElement('link');
        favicon96.rel = 'icon';
        favicon96.sizes = '96x96';
        favicon96.type = 'image/png';
        document.head.appendChild(favicon96);
      }
      favicon96.href = favicons.favicon_96;
    }
  }

  /**
   * Cache branding in localStorage
   * 
   * @param {string} orgSlug - Organization slug
   * @param {Object} branding - Branding configuration
   */
  cacheBranding(orgSlug, branding) {
    try {
      const cacheKey = `branding_${orgSlug}`;
      const cacheData = {
        branding,
        timestamp: Date.now(),
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (e) {
      console.warn('Failed to cache branding:', e);
    }
  }

  /**
   * Get cached branding from localStorage
   * 
   * @param {string} orgSlug - Organization slug
   * @returns {Object|null} Cached branding or null
   */
  getCachedBranding(orgSlug) {
    try {
      const cacheKey = `branding_${orgSlug}`;
      const cached = localStorage.getItem(cacheKey);
      
      if (cached) {
        const cacheData = JSON.parse(cached);
        
        // Cache for 24 hours
        const maxAge = 24 * 60 * 60 * 1000;
        if (Date.now() - cacheData.timestamp < maxAge) {
          return cacheData.branding;
        }
      }
    } catch (e) {
      // Ignore errors
    }
    
    return null;
  }

  /**
   * Get logo URL
   * 
   * @param {boolean} isDark - Whether to get dark mode logo
   * @returns {string} Logo URL
   */
  getLogo(isDark = false) {
    const branding = this.currentBranding || this.getProductDefaultBranding();
    
    if (isDark && branding.logo_dark_url) {
      return branding.logo_dark_url;
    }
    
    return branding.logo_url;
  }

  /**
   * Get color value
   * 
   * @param {string} colorName - Color key (e.g., 'primary', 'secondary')
   * @returns {string} Color hex value
   */
  getColor(colorName) {
    const productDefault = this.getProductDefaultBranding();
    const branding = this.currentBranding || productDefault;
    return branding.colors?.[colorName] || productDefault.colors[colorName];
  }

  /**
   * Get app name
   * 
   * @returns {string} App name
   */
  getAppName() {
    const branding = this.currentBranding || this.getProductDefaultBranding();
    const name = branding.app_name || this.getProductDefaultBranding().app_name;
    // Product rename: "Vet Media Group" → "Vet Media" (keep org prefix if present)
    return typeof name === 'string'
      ? name.replace(/\bVet Media Group\b/g, 'Vet Media')
      : name;
  }

  /**
   * Check if "Powered by" should be shown
   * 
   * @returns {boolean} True if should show "Powered by Praetorian AI"
   */
  shouldShowPoweredBy() {
    const branding = this.currentBranding || this.getProductDefaultBranding();
    return branding.navigation?.show_powered_by !== false;
  }

  /**
   * Get custom footer text
   * 
   * @returns {string} Custom footer text or empty string
   */
  getFooterText() {
    const branding = this.currentBranding || this.getProductDefaultBranding();
    return branding.navigation?.custom_footer_text || '';
  }

  /**
   * Get support contact info
   * 
   * @returns {Object} Support email and phone
   */
  getSupportContact() {
    const branding = this.currentBranding || this.getProductDefaultBranding();
    return {
      email: branding.navigation?.support_email || 'support@get-adi.com',
      phone: branding.navigation?.support_phone || '',
    };
  }

  /**
   * Check if feature should be visible
   * 
   * @param {string} featureName - Feature key (e.g., 'show_carrier_analytics')
   * @returns {boolean} True if feature should be shown
   */
  canShowFeature(featureName) {
    const branding = this.currentBranding || this.defaultBranding;
    
    // Add 'show_' prefix if not present
    const key = featureName.startsWith('show_') ? featureName : `show_${featureName}`;
    
    // Default to true if not specified
    return branding.features?.[key] !== false;
  }

  /**
   * Check if OAuth provider is enabled
   * 
   * @param {string} provider - Provider name (e.g., 'google', 'microsoft')
   * @returns {boolean} True if provider is enabled
   */
  isOAuthProviderEnabled(provider) {
    const branding = this.currentBranding || this.defaultBranding;
    return branding.oauth_providers?.[provider]?.enabled !== false;
  }

  /**
   * Get current organization
   * 
   * @returns {Object|null} Current organization or null
   */
  getCurrentOrganization() {
    return this.currentOrganization;
  }

  /**
   * Get current branding
   * 
   * @returns {Object} Current branding configuration
   */
  getCurrentBranding() {
    return this.currentBranding || this.defaultBranding;
  }

  /**
   * Reset branding to default
   */
  reset() {
    this.currentBranding = null;
    this.currentOrganization = null;
    this.isInitialized = false;
    this.applyBranding();
  }

  /**
   * Force reload branding from server
   * 
   * @param {string} orgSlug - Optional organization slug
   * @returns {Promise<void>}
   */
  async reload(orgSlug = null) {
    this.isInitialized = false;
    
    const slug = orgSlug || this.detectOrganizationSlug();
    
    if (slug) {
      await this.loadOrganizationBranding(slug);
    } else {
      this.currentBranding = this.defaultBranding;
      this.applyBranding();
    }
    
    this.isInitialized = true;
  }

  /**
   * Get complete style object for inline styles
   * Useful for dynamic styling in components
   * 
   * @returns {Object} Style object with CSS properties
   */
  getInlineStyles() {
    const branding = this.currentBranding || this.defaultBranding;
    
    return {
      '--color-primary': branding.colors.primary,
      '--color-secondary': branding.colors.secondary,
      '--color-accent': branding.colors.accent,
      '--color-background': branding.colors.background,
      '--color-surface': branding.colors.surface,
      '--color-text': branding.colors.text,
      '--font-family': branding.typography.font_family,
    };
  }

  /**
   * Generate CSS class name based on current organization
   * Useful for organization-specific styling
   * 
   * @returns {string} CSS class name (e.g., 'org-acme-insurance')
   */
  getOrganizationClassName() {
    if (this.currentOrganization) {
      return `org-${this.currentOrganization.slug}`;
    }
    return 'org-default';
  }
}

// Export singleton instance
export default new WhitelabelBrandingService();

